import React, { useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, FileText } from "lucide-react";
import { extractApiError } from "@/lib/api";

/**
 * PdfUploader
 * Props:
 * - onUpload(file): async function that uploads the file (parent handles API)
 * - currentFilename?: string
 * - disabled?: boolean
 */
export default function PdfUploader({ onUpload, currentFilename = null, disabled = false }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const validateFile = (file) => {
    if (!file) return "No file selected.";
    const typeOk = file.type === "application/pdf";
    const nameOk = String(file.name || "").toLowerCase().endsWith(".pdf");
    if (!typeOk && !nameOk) return "Only PDF files are allowed.";
    return "";
  };

  const triggerInput = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  const handlePicked = async (file) => {
    setError("");
    const v = validateFile(file);
    if (v) {
      setError(v);
      return;
    }

    if (typeof onUpload !== "function") return;

    try {
      setUploading(true);
      await onUpload(file);
    } catch (err) {
      try {
        const apiErr = extractApiError(err);
        setError(apiErr.message || "Upload failed.");
      } catch {
        setError(err?.message || "Upload failed.");
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    handlePicked(f);
    // reset so same file can be selected again
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (disabled || uploading) return;
    const f = e.dataTransfer?.files && e.dataTransfer.files[0];
    if (f) handlePicked(f);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const label = currentFilename ? "Replace" : "Upload PDF";

  return (
    <div>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleFileInput} />

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3"
        data-testid="pdf-uploader-dropzone"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 flex items-center justify-center rounded-md bg-white border border-slate-100">
              <FileText size={18} className="text-slate-600" />
            </div>
            <div>
              <p className="font-medium text-slate-800">{currentFilename || "No document uploaded"}</p>
              <p className="text-xs text-slate-500">PDF only. Drop file here or use the button.</p>
              {error ? <p className="text-xs text-rose-600">{error}</p> : null}
            </div>
          </div>

          <div>
            <Button onClick={triggerInput} disabled={disabled || uploading} size="sm" className="flex items-center gap-2">
              {uploading ? <Loader2 className="animate-spin" size={14} /> : null}
              {label}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
